import { useAuthStore, getFetcher, processAuthResponse } from 'api/swr'
import React, { useState, useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useSearchParams } from 'react-router-dom'
import { toast } from '@/components/ui/use-toast'
import Loader from '@/components/ui/Loader'
import AuthLayout from './AuthLayout'
import button_apple from 'assets/button_apple.svg'
import button_google from 'assets/button_google.svg'

const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const from = location.state?.from?.pathname || '/personal'

  const [isCasdoorLoggingIn, setIsCasdoorLoggingIn] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const isLoggedIn = useAuthStore((state) => state.auth?.token)

  // 在组件加载时立即保存目标页面到 sessionStorage
  useEffect(() => {
    console.log('Login component loaded, location.state:', location.state)
    console.log('from value:', from)
    // 如果有 from 参数且不是默认的 /personal，则保存
    if (location.state?.from?.pathname && location.state.from.pathname !== '/personal') {
      console.log('Saving to sessionStorage:', location.state.from.pathname)
      sessionStorage.setItem('oauth_redirect', location.state.from.pathname)
    }
    // 检查 sessionStorage 中是否有值
    const saved = sessionStorage.getItem('oauth_redirect')
    console.log('Current sessionStorage oauth_redirect:', saved)
  }, [location.state])

  const handleOAuthCallback = async (code) => {
    setIsCasdoorLoggingIn(true)
    try {
      const result = await getFetcher(`/oauth/login?code=${code}`)

      if (result.success && result.data) {
        await processAuthResponse(result.data)
        setIsCasdoorLoggingIn(false)

        const savedRedirect = sessionStorage.getItem('oauth_redirect')
        sessionStorage.removeItem('oauth_redirect')
        const redirectTo = savedRedirect || from
        navigate(redirectTo, { replace: true })
      } else {
        throw new Error(result.error || result.message || 'OAuth login failed')
      }
    } catch (error) {
      console.error('OAuth login failed:', error)
      const errorMessage =
        error.message ||
        error.field?.message ||
        'OAuth login failed. Please try again.'
      toast({
        variant: 'destructive',
        title: '登录失败',
        description: errorMessage,
      })
      setIsCasdoorLoggingIn(false)
    }
  }

  useEffect(() => {
    const code = searchParams.get('code')
    const error = searchParams.get('error')

    // 如果有 error 参数，显示错误消息
    if (error) {
      toast({
        title: '提示',
        description: decodeURIComponent(error),
      })
      // 保留 error 参数在 URL 中，不清除
    }

    if (code) {
      handleOAuthCallback(code)
    } else if (!isLoggedIn && !isRedirecting && !code && !error) {
      // 只有在没有 code 和 error 参数时才自动跳转到 OAuth 登录
      const redirectToOAuth = async () => {
        try {
          setIsRedirecting(true)
          // 从 sessionStorage 读取，如果没有则使用 from
          const savedRedirect = sessionStorage.getItem('oauth_redirect')
          console.log('Before OAuth redirect - savedRedirect:', savedRedirect, 'from:', from)
          const redirectTarget = savedRedirect || (from !== '/personal' ? from : null)
          if (redirectTarget) {
            sessionStorage.setItem('oauth_redirect', redirectTarget)
            console.log('Saving redirect target before auto OAuth:', redirectTarget)
          }
          // 再次检查
          console.log('Final sessionStorage check:', sessionStorage.getItem('oauth_redirect'))
          
          const data = await getFetcher('/oauth/url')
          if (data.url) {
            window.location.href = data.url
          }
        } catch (error) {
          console.error('Failed to get OAuth login URL:', error)
          setIsRedirecting(false)
        }
      }
      redirectToOAuth()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoggedIn) {
    // 从 sessionStorage 获取保存的跳转地址
    const savedRedirect = sessionStorage.getItem('oauth_redirect')
    sessionStorage.removeItem('oauth_redirect')
    const redirectTo = savedRedirect || from
    console.log('Already logged in, redirecting to:', redirectTo)
    return <Navigate to={redirectTo} replace />
  }

  if (isCasdoorLoggingIn || isRedirecting) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center justify-center py-8">
          <Loader>
            {isCasdoorLoggingIn ? 'logging in...' : 'redirecting...'}
          </Loader>
        </div>
      </div>
    )
  }

  return (
    <AuthLayout
      title="Neku - Avatar Maker"
      description="Create original character generators with your own designs."
      showOAuth={true}
    >
      <div className="flex flex-col gap-6">
        <p className="text-base text-slate-700 leading-relaxed">
          Join us to expand your imagination.
          <br />
          Share your artwork with everyone.
        </p>

        <div className="space-y-4">
          <p className="text-sm font-medium text-slate-900">下载我们的应用</p>
          <div className="flex items-center gap-4">
            <a
              href="https://apps.apple.com/cn/app/neku-oc-avatar-maker/id1630343674"
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={button_apple}
                className="cursor-pointer h-12"
                alt="Download on App Store"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.stickermobi.avatarmaker"
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={button_google}
                className="cursor-pointer h-12"
                alt="Get it on Google Play"
              />
            </a>
          </div>
        </div>
      </div>
    </AuthLayout>
  )
}

export default Login
